/**
 * Email Template API Contracts
 *
 * Wire schemas for the school-scoped email template store (U5 of the email
 * broadcast builder). Templates are a distinct reusable artifact — the Puck
 * `Data` a school saves to reuse across broadcasts — NOT a flagged broadcast.
 *
 * The template `schema` is the email builder's Puck `Data`, validated against
 * `EmailDesignSchema` (the same silent-block-drop guard the broadcast
 * `metadata.design` uses). Backend Zod schemas at
 * `apps/backend/src/api/messaging/v1/use-cases/email-templates/**` mirror
 * these — update both in lockstep.
 */

import { z } from 'zod'

import { EmailDesignSchema } from '../../schemas/entities/email-design'

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

/**
 * Full template, returned by get-single — carries the stored Puck `Data` so a
 * client can start a new email from it (R6: returned unchanged).
 */
export const EmailTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  schema: EmailDesignSchema,
  createdAt: z.string(),
  updatedAt: z.string()
})
export type EmailTemplate = z.infer<typeof EmailTemplateSchema>

/**
 * Lightweight list item — omits the (potentially large) design blob so the
 * gallery list stays cheap. The full `schema` is fetched via get-single.
 */
export const EmailTemplateSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
})
export type EmailTemplateSummary = z.infer<typeof EmailTemplateSummarySchema>

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const CreateEmailTemplateRequestSchema = z.object({
  name: z.string().min(1, 'Template name is required').max(255),
  description: z.string().max(2000).optional(),
  // This is the canonical design contract. The backend handler schema
  // (`email-templates/schema.ts`) intentionally accepts `schema` as
  // `z.unknown()` and defers design validation to the service layer, so an
  // invalid design surfaces as a 422 (INVALID_INPUT) from the use-case rather
  // than a 400 (INVALID_PARAMS) at the handler boundary. Clients should validate
  // against EmailDesignSchema here before sending.
  schema: EmailDesignSchema
})
export type CreateEmailTemplateRequest = z.infer<typeof CreateEmailTemplateRequestSchema>

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export const CreateEmailTemplateResponseSchema = z.object({
  message: z.string(),
  data: EmailTemplateSchema
})
export type CreateEmailTemplateResponse = z.infer<typeof CreateEmailTemplateResponseSchema>

export const ListEmailTemplatesResponseSchema = z.object({
  data: z.array(EmailTemplateSummarySchema)
})
export type ListEmailTemplatesResponse = z.infer<typeof ListEmailTemplatesResponseSchema>

export const GetEmailTemplateResponseSchema = z.object({
  data: EmailTemplateSchema
})
export type GetEmailTemplateResponse = z.infer<typeof GetEmailTemplateResponseSchema>

export const DeleteEmailTemplateResponseSchema = z.object({
  message: z.string()
})
export type DeleteEmailTemplateResponse = z.infer<typeof DeleteEmailTemplateResponseSchema>
